import React, { useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import {
    Badge,
    Button,
    RegionState,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCellProps,
    TableRow,
} from '@freelancelabs/components';

import Card from '../../components/Card';
import { OrganizationKind } from '../../graphql/__generated__/schema';
import { AssignableDashboardsDocument } from '../../graphql/__generated__/types';
import { refusalCode } from '../../graphql/refusalCode';
import ArchiveDashboardDialog from './ArchiveDashboardDialog';
import DashboardDialog, { DashboardDialogMode } from './DashboardDialog';

interface Props {
    accountId: string;
    accountName: string;
    /** Freelance.com is `internal`; every client account is not. */
    kind: OrganizationKind | null | undefined;
    /**
     * The account's technical Salesforce id, handed down to the declare dialog so it can
     * show it read-only for confirmation. Null when the account has no source anchor.
     */
    salesforceId: string | null | undefined;
}

/** The entry a dialog is currently acting on, and what the dialog is for. */
type Editing = { mode: DashboardDialogMode; entry?: { id: string; name: string } | null };

/**
 * The dashboard catalogue of **one account**: PCG-79.
 *
 * **Why it lives here, next to the portal access, on the account sheet.** A dashboard
 * is the object of an account: it carries an `organization_id` and a composite foreign
 * key to the membership, and its right is `manage_accounts`, literally the gate that
 * opens that same account to the portal. So it belongs on the screen where an account
 * is the subject. That screen was the users list filtered on a single account until
 * PCG-141, which is the « Comptes » module this comment used to be waiting for; the
 * panel moved with the other two rather than being left behind on a filter.
 *
 * The partner lists went the other way, and for a reason that is not symmetry. See
 * `PartnerListsPage`: a partner list carries no `organization_id` and belongs to no
 * client, so hanging it off an account would state that one owns it.
 *
 * **Attention, the catalogue is read through `assignableDashboards`, which is not an
 * administrator's view.** It returns live entries only (`archived_at IS NULL`) and
 * five fields, none of which is the Metabase identifier or a parameter slug. So this
 * panel can list, declare, rename and archive, but it can neither show an archived
 * entry nor edit anything but the label. The missing piece is a read path in
 * `client-hub-api`, not something to reconstruct here.
 *
 * **Attention, nothing is hidden on a right the screen cannot check.** `viewer` exposes the
 * role but not the permissions behind it, so the front cannot know whether this
 * operator holds `manage_accounts`. The controls therefore stay visible and the
 * refusal is rendered when it comes, naming the right that is missing: a silently
 * removed button reads as a feature that does not exist.
 */
const DashboardCataloguePanel = ({ accountId, accountName, kind, salesforceId }: Props) => {
    const [editing, setEditing] = useState<Editing | null>(null);
    const [archiving, setArchiving] = useState<{ id: string; name: string } | null>(null);

    /**
     * Narrowed to this one account, so the panel lists what belongs to the account the
     * screen is about. Skipped on Freelance.com: the internal organisation carries no
     * client dashboard and every gesture on it ends in
     * `INTERNAL_ORGANIZATION_NOT_ALLOWED`.
     */
    const internal = kind === OrganizationKind.Internal;
    const { data, loading, error } = useQuery(AssignableDashboardsDocument, {
        variables: { organizationIds: [accountId] },
        skip: internal,
    });

    if (internal) {
        return null;
    }

    const dashboards = (data?.assignableDashboards?.dashboards ?? []).filter(Boolean);

    const columns: TableHeaderCellProps[] = [
        { id: 'name', label: c('Dashboard catalogue').t`Label read by the client`, minWidth: 240 },
        { id: 'perimeter', label: c('Dashboard catalogue').t`Perimeter`, minWidth: 200 },
        { id: 'actions', label: '', minWidth: 190, textAlign: 'end' },
    ];

    /**
     * The refusal of the **read**, which is not one of the six mutations: this query
     * is bounded by `manage_users`, where declaring takes `manage_accounts`. The two
     * can differ, so « vous ne pouvez pas déclarer » would be the wrong sentence here.
     */
    const readRefusal = () => {
        switch (refusalCode(error)) {
            case 'MISSING_PERMISSION':
                return c('Dashboard catalogue')
                    .t`Your role does not let you manage users, and this catalogue is read under that permission. It was refused, not answered: it is not empty, it was not read.`;
            case 'FORBIDDEN_OUT_OF_SCOPE':
                return c('Dashboard catalogue')
                    .t`This account is outside your portfolio, so its catalogue was refused rather than answered.`;
            default:
                return c('Dashboard catalogue')
                    .t`The catalogue of this account could not be read, so what it holds is unknown — this is not the same as holding nothing.`;
        }
    };

    return (
        <>
            <Card
                title={c('Dashboard catalogue').t`Dashboards of this account`}
                counter={error ? undefined : dashboards.length}
                actions={
                    <Button category="weak" onClick={() => setEditing({ mode: 'declare' })}>
                        {c('Dashboard catalogue').t`Declare a dashboard…`}
                    </Button>
                }
            >
                <Table>
                    <TableHeader resizable cells={columns} />
                    <TableBody loading={loading} loadingRows={2} loadingColumns={columns.length}>
                        {dashboards.map((dashboard) => {
                            const id = dashboard.id as string;
                            const name = dashboard.name ?? '';
                            /**
                             * False means the entry declares no locked parameter, so the
                             * token maker refuses to sign and the habilitation would never
                             * open anything. Our own declarations cannot produce it: the
                             * API refuses them, so a false here is a row written by hand
                             * or by a future importer. Shown rather than filtered: hiding
                             * it would make the catalogue look emptier than it is.
                             */
                            const locked = dashboard.perimeterLocked === true;
                            return (
                                <TableRow key={id}>
                                    <TableCell>
                                        <span className="font-medium">{name}</span>
                                    </TableCell>
                                    <TableCell>
                                        {locked ? (
                                            <Badge category="success" size="small">
                                                {c('Dashboard catalogue').t`Locked to this account`}
                                            </Badge>
                                        ) : (
                                            <span className="flex flex-col gap-1">
                                                <Badge category="warning" size="small">
                                                    {c('Dashboard catalogue').t`No perimeter parameter`}
                                                </Badge>
                                                <span className="text-xs text-warning-600">
                                                    {c('Dashboard catalogue')
                                                        .t`Cannot be granted: it would be signed for nobody and open nothing. Archive it and declare it again with its parameter.`}
                                                </span>
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-end">
                                        <span className="inline-flex gap-2">
                                            <Button
                                                category="weak"
                                                size="small"
                                                onClick={() => setEditing({ mode: 'rename', entry: { id, name } })}
                                            >
                                                {c('Action').t`Rename`}
                                            </Button>
                                            <Button
                                                category="weak"
                                                size="small"
                                                onClick={() => setArchiving({ id, name })}
                                            >
                                                {c('Action').t`Archive`}
                                            </Button>
                                        </span>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>

                {/* Three states, told apart. An error rendered as an absence would send
                    the operator away sure the account has no dashboard. */}
                {error && (
                    <RegionState
                        tone="error"
                        title={c('Dashboard catalogue').t`This catalogue could not be read.`}
                        description={readRefusal()}
                    />
                )}
                {!loading && !error && dashboards.length === 0 && (
                    <RegionState
                        title={c('Dashboard catalogue').t`No dashboard is declared on this account.`}
                        description={c('Dashboard catalogue')
                            .t`That is the ordinary state of a new account. Declare an entry to be able to grant it.`}
                    />
                )}
            </Card>

            {editing && (
                <DashboardDialog
                    mode={editing.mode}
                    accountId={accountId}
                    accountName={accountName}
                    salesforceId={salesforceId}
                    entry={editing.entry}
                    onClose={() => setEditing(null)}
                />
            )}
            {archiving && (
                <ArchiveDashboardDialog
                    entry={archiving}
                    accountName={accountName}
                    onClose={() => setArchiving(null)}
                />
            )}
        </>
    );
};

export default DashboardCataloguePanel;
